import { getStripe, getStripePublishableKey } from '@/lib/stripeClient';
import type { Stripe } from '@stripe/stripe-js';
import { toast } from 'sonner';

export type CheckoutSessionPayload = {
  sessionId: string;
  url: string;
  provider?: 'manual' | 'stripe';
};

/**
 * Wave 3: manual_accept returns an app URL (pending). Stripe (when configured)
 * uses Stripe.js or Checkout URL. Never toast fake "success" without a session.
 * @returns true if navigation was started; false if caller should stop loading
 */
export async function redirectToSubscriptionCheckout(
  session: CheckoutSessionPayload
): Promise<boolean> {
  const isManual =
    session.provider === 'manual' ||
    String(session.sessionId || '').startsWith('manual_') ||
    String(session.sessionId || '') === 'admin_bypass';

  if (isManual) {
    if (session.url) {
      window.location.href = session.url;
      return true;
    }
    toast.error('Checkout session URL missing. Please try again.');
    return false;
  }

  const stripe = await getStripe();
  if (stripe && session.sessionId && !session.sessionId.startsWith('manual_')) {
    // stripe-js typings vary by version; Checkout Session redirect remains supported
    const { error } = await (
      stripe as Stripe & {
        redirectToCheckout: (opts: {
          sessionId: string;
        }) => Promise<{ error?: { message?: string } }>;
      }
    ).redirectToCheckout({
      sessionId: session.sessionId,
    });
    if (error) {
      // Prefer hosted Checkout URL if Stripe.js redirect fails
      if (session.url) {
        window.location.href = session.url;
        return true;
      }
      toast.error(error.message || 'Checkout redirect failed');
      return false;
    }
    return true;
  }

  if (session.url) {
    window.location.href = session.url;
    return true;
  }

  if (getStripePublishableKey()) {
    toast.error(
      'Checkout could not load Stripe.js. Allow js.stripe.com or use a network without blocking it; your host may still provide a direct checkout URL.'
    );
  } else {
    toast.message(
      'Payment request recorded as pending. An admin will grant access shortly.'
    );
  }
  return false;
}
